import { yupResolver } from '@hookform/resolvers/yup';
import * as Dialog from '@radix-ui/react-dialog';
import { AxiosError, AxiosInstance } from 'axios';

import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { errorMessageAdapter } from '../../../adapters';
import { MainCustomButton } from '../../../components/Buttons';
import { Input } from '../../../components/atoms';
import { CustomInput } from '../../../components/inputs';
import { DocumentService } from '../../../services/DocumentService';
import { useAuth } from '../../../store/auth';
import { errorToast, successToast } from '../../../utils/toasts';
import { useApi } from '../../../services/hooks/apiHook';
import { countries } from '../../../constants/objects';
import { transformDate } from '../../../utils/date';
import { addMonths, parseISO, isBefore, differenceInDays } from 'date-fns';

interface IPassportUpload {
  director: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: () => void;
}

interface PassportFormData {
  passportNo: string;
  expiryDate: string;
  passportIssuingCountry: string;
  issueDate: string;
  passport: File | null;
}

const today = new Date();
const sixMonthsFromNow = addMonths(today, 6);

function validateFileSize(value: any): boolean {
  if (!value || !value.name) return true;
  return value.size <= 3000000;
}

function validateFileType(value: any): boolean {
  if (!value || !value.name) return true;
  return (
    value.type === 'application/pdf' ||
    value.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  );
}

const PassportUploadModal = ({ director, open, onOpenChange, onChange }: IPassportUpload) => {
  const { i18n, t } = useTranslation();
  const { token } = useAuth();
  const api: AxiosInstance = useApi();

  const [fileNamePassport, setFileNamePassport] = React.useState<string | null>();
  const [loading, setLoading] = React.useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    passportNo: Yup.string().required(t('labels.passportNumberRequired') || ''),
    expiryDate: Yup.string().required(t('labels.expiryDateRequired') || 'Expiry date is required'),
    passportIssuingCountry: Yup.string().required(t('labels.issuingCountryRequired') || ''),
    issueDate: Yup.string().required(t('labels.issueDateRequired') || ''),
    passport: Yup.mixed()
      .test('fileSize', t('OnboardingCreateAccountErrorFileSize') || '', validateFileSize)
      .test('fileType', t('OnboardingCreateAccountErrorFileType') || '', validateFileType)
      .test('fileRequired', t('labels.passportFileRequired') || 'Por favor, selecione um arquivo de passaporte', function(value) {
        const isValid = !!(value && (value as File).name && (value as File).size > 0);
        return isValid;
      })
  });

  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    reset
  } = useForm<PassportFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      passportNo: '',
      expiryDate: '',
      passportIssuingCountry: '',
      issueDate: '',
      passport: null
    }
  });

  useEffect(() => {
    if (!open) return;
    
    // Se existe um passaporte, carregar os dados
    if (director?.passports?.[0]) {
      const passport = director.passports[0];
      
      // Converter datas para formato YYYY-MM-DD para os campos de input
      const formatDateForInput = (dateString: string) => {
        if (!dateString) return '';
        
        // Se já está no formato YYYY-MM-DD, retorna como está
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateString;
        }
        
        // Se está no formato DD/MM/YYYY, converte para YYYY-MM-DD
        if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          const [day, month, year] = dateString.split('/');
          return `${year}-${month}-${day}`;
        }
        
        // Se está no formato YYYY/MM/DD, converte para YYYY-MM-DD
        if (dateString.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
          return dateString.replace(/\//g, '-');
        }
        
        return dateString;
      };

      setValue('passportNo', passport.number || '');
      setValue('expiryDate', formatDateForInput(passport.expiry_date || ''));
      setValue('issueDate', formatDateForInput(passport.issuing_date || ''));
      setValue('passportIssuingCountry', passport.issuing_country || '');
    } else {
      // Se não existe passaporte, limpar o formulário
      setFileNamePassport('');
      reset();
    }
  }, [open, director, setValue, reset]);

  const handleFileChangePassport = (e: any) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setFileNamePassport(file.name);
      setValue('passport', file);
    } else {
      setFileNamePassport('');
      setValue('passport', null);
    }
  };

  const handleSubmitForm: SubmitHandler<PassportFormData> = async (data) => {
    try {
      // Verificar se o passaporte atual permite upload (vencido ou 30 dias antes)
      // Se não houver passaporte, permite upload (primeira vez)
      if (director?.passports?.[0]?.expiry_date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiryDate = parseISO(director.passports[0].expiry_date);
        expiryDate.setHours(0, 0, 0, 0);
        const daysLeft = differenceInDays(expiryDate, today);
        
        // Permite upload se estiver vencido OU se faltarem 30 dias ou menos para vencer
        if (daysLeft > 30) {
          errorToast(
            t('labels.passportUploadOnlyNearExpiry') || 
            `You can only upload a new passport when the current one is expired or within 30 days of expiration. Current passport expires in ${daysLeft} days.`
          );
          return;
        }
      }
      // Se não houver passaporte ou estiver dentro do prazo permitido, permite upload normalmente

      setLoading(true);

      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Language': i18n.language.toLowerCase()
      };

      const passportValue = getValues('passport');

      if (passportValue && passportValue.name && passportValue.size > 0) {
        const formData = new FormData();
        formData.append('shareholder_slug', director.slug);
        
        // Se existe um passaporte, usar o slug dele, senão deixar vazio para criar novo
        const documentSlug = director.passports?.[0]?.slug || '';
        formData.append('document_slug', documentSlug);
        
        formData.append('action', '3'); // Ação para atualizar passaporte
        formData.append('document', passportValue);
        
        // Adicionar informações do passaporte
        formData.append('number', data.passportNo);
        
        // Converter datas para formato YYYY-MM-DD
        const formatDateToYYYYMMDD = (dateInput: any) => {
          if (!dateInput) return '';
          
          // Se é um objeto Date, converte para string
          if (dateInput instanceof Date) {
            return dateInput.toISOString().split('T')[0];
          }
          
          // Se é string, verifica o formato
          const dateString = String(dateInput);
          
          // Se já está no formato YYYY-MM-DD, retorna como está
          if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return dateString;
          }
          // Se está no formato YYYY/MM/DD, converte para YYYY-MM-DD
          if (dateString.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
            return dateString.replace(/\//g, '-');
          }
          // Se está no formato DD/MM/YYYY, converte para YYYY-MM-DD
          if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            const [day, month, year] = dateString.split('/');
            return `${year}-${month}-${day}`;
          }
          
          return dateString;
        };

        const formattedIssueDate = formatDateToYYYYMMDD(data.issueDate);
        const formattedExpiryDate = formatDateToYYYYMMDD(data.expiryDate);

        formData.append('issuing_date', formattedIssueDate);
        formData.append('issuing_country', data.passportIssuingCountry);
        formData.append('expiry_date', formattedExpiryDate);

        const response = await DocumentService.resendDocument(api, formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        successToast(t('labels.passportUpdatedSuccessfully') || 'Passport updated successfully');
      } else {
        errorToast(t('labels.passportFileRequired') || 'Por favor, selecione um arquivo de passaporte');
        return;
      }
    } catch (error) {
      console.error('❌ Erro geral:', error);
      errorToast(errorMessageAdapter(error as AxiosError));
    } finally {
      setLoading(false);
      reset();
      onOpenChange(false);
      onChange();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal className="z-20">
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm data-[state=open]:animate-overlayShow" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-20 max-h-[90vh] w-[95vw] max-w-[900px] translate-x-[-50%] translate-y-[-50%] overflow-y-auto rounded-2xl bg-white shadow-2xl focus:outline-none data-[state=open]:animate-contentShow">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                  <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {t('labels.uploadPassportDocument') || 'Upload Passport Document'}
                  </h2>
                  <p className="text-blue-100">
                    {t('labels.uploadPassportDocumentDescription') || 'Upload your passport document'}
                  </p>
                </div>
              </div>
              <Dialog.Close asChild>
                <button className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm text-white transition-colors hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </Dialog.Close>
            </div>
          </div>

          <form onSubmit={handleSubmit(handleSubmitForm)} className="p-8">
            <div className="space-y-8">
              {/* Informações do Passaporte */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-100 to-indigo-100 px-6 py-4 border-b border-blue-200">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-200">
                      <svg className="h-4 w-4 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-blue-900">
                      {t('labels.passportInformation')}
                    </h3>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  <CustomInput
                    label={t('labels.passportNumber')}
                    id="passportNo"
                    register={register}
                    error={errors.passportNo?.message}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Controller
                      name="expiryDate"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Input
                          label={t('labels.expiryDate') || 'Expiry Date'}
                          type="date"
                          id="expiryDate"
                          {...field}
                          error={errors.expiryDate?.message}
                        />
                      )}
                    />
                    
                    <Controller
                      name="issueDate"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Input
                          label={t('labels.issueDate') || 'Issue Date'}
                          type="date"
                          id="issueDate"
                          {...field}
                          error={errors.issueDate?.message}
                        />
                      )}
                    />
                  </div>
                  
                  <div className="flex w-full flex-col gap-2">
                    <label
                      htmlFor="passportIssuingCountry"
                      className={`${errors.passportIssuingCountry?.message ? 'text-red-500' : 'text-gray-700'} text-sm font-medium`}
                    >
                      {t('labels.passportIssuingCountry')}
                    </label>
                    <select
                      id="passportIssuingCountry"
                      className={`${
                        errors.passportIssuingCountry?.message 
                          ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-500'
                      } h-12 w-full rounded-lg border px-4 text-sm outline-none transition-colors focus:ring-2 focus:ring-opacity-20`}
                      {...register('passportIssuingCountry')}
                      onChange={(e) => setValue('passportIssuingCountry', e.target.value)}
                    >
                      <option value="" disabled>
                        {t('labels.selectCountry')}
                      </option>
                      {countries[i18n.language as keyof typeof countries]?.map((country: any) => (
                        <option key={country.name} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    {errors.passportIssuingCountry?.message && (
                      <span className="text-xs text-red-500">{errors.passportIssuingCountry.message}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Upload do Documento */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-100 to-indigo-100 px-6 py-4 border-b border-blue-200">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-200">
                      <svg className="h-4 w-4 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-blue-900">
                      {t('labels.documentUpload')}
                    </h3>
                  </div>
                </div>
                <div className="p-6">
                  <label className="group relative flex h-64 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-blue-300 bg-white/50 transition-all duration-200 hover:border-blue-400 hover:bg-blue-50/50 focus-within:border-blue-500 focus-within:bg-blue-50">
                    {!fileNamePassport ? (
                      <div className="flex flex-col items-center space-y-4">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 group-hover:bg-blue-200 transition-colors">
                          <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                        </div>
                        <div className="flex flex-col items-center space-y-2 text-center">
                          <p className="text-lg font-semibold text-blue-700 group-hover:text-blue-800">
                            {t('labels.uploadYourDocument') || 'Upload your document'}
                          </p>
                          <p className="text-sm text-blue-600 group-hover:text-blue-700">
                            {t('labels.orSearchInComputer') || 'or search in your computer'}
                          </p>
                          <div className="flex items-center justify-center gap-2 text-xs text-blue-500">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span>{t('labels.supportedFormats')}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center space-y-4">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                          <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="flex flex-col items-center space-y-2 text-center">
                          <p className="text-lg font-semibold text-green-700">
                            {t('labels.fileSelected') || 'Arquivo selecionado'}
                          </p>
                          <p className="text-sm font-medium text-gray-800">
                            {fileNamePassport}
                          </p>
                          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span>{t('labels.clickToChangeFile') || 'Clique para trocar o arquivo'}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <input
                      id="passport"
                      type="file"
                      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      onChange={handleFileChangePassport}
                      accept=".pdf,.doc,.docx"
                    />
                  </label>
                  
                  {errors.passport?.message && (
                    <div className="mt-3 flex items-center justify-center gap-2 text-red-500">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm">{errors.passport.message}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-20 transition-colors"
              >
                {t('labels.cancel') || 'Cancelar'}
              </button>
              
              <MainCustomButton 
                text={t('labels.uploadPassport')} 
                type="submit" 
                loading={loading}
                className="px-8 py-3"
              />
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default PassportUploadModal;